import { useState } from 'react'
import { ThumbsDown, ThumbsUp } from 'lucide-react'
import { companionClient } from '@/services/api/api-client'

type FeedbackRating = 'up' | 'down'

interface AnalystFeedbackProps {
  answerEventId: string
}

async function submitFeedback(
  answerEventId: string,
  rating: FeedbackRating,
  setSelected: (rating: FeedbackRating) => void,
  setInFlight: (inFlight: boolean) => void,
): Promise<void> {
  setInFlight(true)
  try {
    // Only latch the thumb when the server actually recorded it — a 4xx/5xx
    // or a network throw (both land in the catch) leaves it unlatched so the
    // user can retry instead of seeing a false "recorded" state. The body is
    // ignored, so stream() (auth + throw-on-non-2xx, no JSON parse).
    await companionClient.stream('/v1/analyst/feedback', {
      answer_event_id: answerEventId,
      rating,
    })
    setSelected(rating)
  } catch {
    // leave unlatched for retry
  } finally {
    setInFlight(false)
  }
}

/**
 * Thumbs on an analyst answer, recorded against its answer event. Shared by
 * the companion's conversation and the research page's answer turns.
 */
export function AnalystFeedback({ answerEventId }: AnalystFeedbackProps) {
  const [selected, setSelected] = useState<FeedbackRating | null>(null)
  const [inFlight, setInFlight] = useState(false)

  const handleFeedback = async (rating: FeedbackRating) => {
    if (selected !== null || inFlight) return
    await submitFeedback(answerEventId, rating, setSelected, setInFlight)
  }

  return (
    <div className="flex items-center gap-1 px-3 pb-2" data-testid="analyst-feedback">
      <button
        type="button"
        aria-label="Thumbs up"
        disabled={selected !== null || inFlight}
        onClick={() => handleFeedback('up')}
        className={[
          'flex h-6 w-6 items-center justify-center rounded transition-colors',
          selected === 'up'
            ? 'text-emerald-400'
            : 'text-muted-foreground hover:text-foreground disabled:opacity-40',
        ].join(' ')}
      >
        <ThumbsUp size={13} />
      </button>
      <button
        type="button"
        aria-label="Thumbs down"
        disabled={selected !== null || inFlight}
        onClick={() => handleFeedback('down')}
        className={[
          'flex h-6 w-6 items-center justify-center rounded transition-colors',
          selected === 'down'
            ? 'text-rose-400'
            : 'text-muted-foreground hover:text-foreground disabled:opacity-40',
        ].join(' ')}
      >
        <ThumbsDown size={13} />
      </button>
    </div>
  )
}
